// ===== BEGIN FILE: src/astro/feeds/userRuleLoader.ts =====
import type { FeedRule } from '../../components/RuleBuilder';
import type { Rule } from './rules';
import type { DashaContext, TransitContext, HouseNum, PlanetName } from './types';
import { PLANET_NATURE } from './types';
import { weightByLevel, natureWeight, normHouseScore, transitReinforcement, aggregateScore } from './scoring';
import { supabase } from '../../utils/supabaseClient';
import { evaluateNatalConditions, type NatalChartData } from './natalEvaluator';

// Global natal chart data for current user session
// This should be set when user's chart is calculated
let currentNatalChart: NatalChartData | null = null;

/**
 * Set the natal chart data for rule evaluation
 * Call this when user's chart is calculated/loaded
 */
export function setNatalChartData(chart: NatalChartData | null) {
  currentNatalChart = chart;
}

/**
 * Get current natal chart data
 */
export function getNatalChartData(): NatalChartData | null {
  return currentNatalChart;
}

/**
 * Load user-defined rules from Supabase
 */
export async function loadUserRules(): Promise<FeedRule[]> {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    // Fetch rules from Supabase
    const { data, error } = await supabase
      .from('feed_rules')
      .select('*')
      .eq('user_id', user.id)
      .eq('enabled', true)
      .order('priority', { ascending: false });

    if (error) {
      console.error('Failed to load user rules from Supabase:', error);
      return [];
    }

    if (!data) return [];

    // Convert from database format to FeedRule format
    const rules: FeedRule[] = data.map(row => ({
      id: row.id,
      enabled: row.enabled,
      name: row.name,
      description: row.description || undefined,
      lifeAreas: row.life_areas,
      conditions: row.conditions,
      templateKey: row.template_key,
      priority: row.priority,
      severityOverride: row.severity_override || undefined,
      activeFrom: row.active_from || undefined,
      activeTo: row.active_to || undefined,
      prediction: row.prediction || undefined,
      remedies: row.remedies || undefined,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    }));

    // Filter by active date range
    const now = new Date();
    return rules.filter(r => {
      if (r.activeFrom && new Date(r.activeFrom) > now) return false;
      if (r.activeTo && new Date(r.activeTo) < now) return false;
      return true;
    });
  } catch (e) {
    console.error('Failed to load user rules:', e);
    return [];
  }
}

/**
 * Get all activated houses from dasha context
 */
function getActivatedHouses(dasha: DashaContext): Set<HouseNum> {
  const activated = new Set<HouseNum>();
  for (const level of dasha.levels) {
    activated.add(level.occupies);
    level.rules.forEach(h => activated.add(h));
    (level.aspects || []).forEach(h => activated.add(h));
  }
  return activated;
}

/**
 * Convert a user-defined FeedRule to an executable Rule
 */
export function convertToExecutableRule(feedRule: FeedRule): Rule {
  const triggers = (dasha: DashaContext, transits: TransitContext): boolean => {
    // 0. Check natal chart conditions first (if any)
    const hasNatalConditions = 
      (feedRule.conditions.planet_in_house && feedRule.conditions.planet_in_house.length > 0) ||
      (feedRule.conditions.lordship && feedRule.conditions.lordship.length > 0) ||
      (feedRule.conditions.aspects && feedRule.conditions.aspects.length > 0) ||
      (feedRule.conditions.conjunctions && feedRule.conditions.conjunctions.length > 0) ||
      (feedRule.conditions.exchanges && feedRule.conditions.exchanges.length > 0);
    
    if (hasNatalConditions) {
      if (!currentNatalChart) {
        console.warn(`Rule "${feedRule.name}" has natal conditions but no chart data available`);
        return false;
      }
      
      // Check if chart type matches
      if (feedRule.conditions.chart_type && feedRule.conditions.chart_type !== currentNatalChart.chartType) {
        console.warn(`Rule "${feedRule.name}" requires ${feedRule.conditions.chart_type} chart but ${currentNatalChart.chartType} is loaded`);
        return false;
      }
      
      const natalEval = evaluateNatalConditions(feedRule.conditions, currentNatalChart);
      if (!natalEval.matches) {
        return false;
      }
    }
    
    // 1. Check dasha level filter
    if (feedRule.conditions.dasha_levels && feedRule.conditions.dasha_levels.length > 0) {
      const activeLevels = dasha.levels.map(l => l.level);
      const hasMatchingLevel = feedRule.conditions.dasha_levels.some(dl => activeLevels.includes(dl));
      if (!hasMatchingLevel) return false;
    }

    // 2. Check house activations
    if (feedRule.conditions.house_activations && feedRule.conditions.house_activations.length > 0) {
      const activatedHouses = getActivatedHouses(dasha);

      // Note: FeedRuleConditions doesn't have house_activation_mode yet, defaulting to 'any'
      const mode = 'any';
      if (mode === 'any') {
        const hasAny = feedRule.conditions.house_activations.some(h => activatedHouses.has(h as HouseNum));
        if (!hasAny) return false;
      } else {
        // mode === 'all'
        const hasAll = feedRule.conditions.house_activations.every(h => activatedHouses.has(h as HouseNum));
        if (!hasAll) return false;
      }
    }

    // 3. Check dasha planets filter (not yet in FeedRuleConditions, skipping for now)
    // TODO: Add dasha_planets to FeedRuleConditions

    // 4. Check planetary natures filter
    if (feedRule.conditions.planetary_nature && feedRule.conditions.planetary_nature.length > 0) {
      const activeNatures = dasha.levels.map(l => PLANET_NATURE[l.planet as PlanetName]);
      const hasMatchingNature = feedRule.conditions.planetary_nature.some((n: string) => activeNatures.includes(n as any));
      if (!hasMatchingNature) return false;
    }

    // 5. Check transit triggers
    if (feedRule.conditions.transit_triggers && feedRule.conditions.transit_triggers.length > 0) {
      const transitMatches = feedRule.conditions.transit_triggers.some(tt => {
        return transits.positions.some(pos => 
          pos.planet === tt.planet && tt.houses.includes(pos.house)
        );
      });
      
      // Note: FeedRuleConditions doesn't have transit_required yet
      // If any transit trigger is defined, we assume at least one should match
      if (!transitMatches) {
        return false;
      }
    }

    return true;
  };

  const score = (dasha: DashaContext, transits: TransitContext): { value: number; notes: string[] } => {
    const notes: string[] = [];
    const contrib: number[] = [];

    // Determine which houses to score
    const housesToScore = feedRule.conditions.house_activations || [];

    for (const level of dasha.levels) {
      // Skip if dasha level filter doesn't match
      if (feedRule.conditions.dasha_levels && 
          feedRule.conditions.dasha_levels.length > 0 && 
          !feedRule.conditions.dasha_levels.includes(level.level)) {
        continue;
      }

      // Skip if planet filter doesn't match (not yet in FeedRuleConditions)
      // TODO: Add dasha_planets to FeedRuleConditions

      // Skip if nature filter doesn't match
      if (feedRule.conditions.planetary_nature && 
          feedRule.conditions.planetary_nature.length > 0 && 
          !feedRule.conditions.planetary_nature.includes(PLANET_NATURE[level.planet as PlanetName] as any)) {
        continue;
      }

      const lvlW = weightByLevel(level.level);
      const naturesgn = natureWeight(level.planet as PlanetName);

      // Score all activated houses for this dasha level
      const activatedHouses = new Set<HouseNum>([level.occupies, ...level.rules, ...(level.aspects || [])]);
      
      for (const house of activatedHouses) {
        // If user specified houses, only score those
        if (housesToScore.length > 0 && !housesToScore.includes(house)) {
          continue;
        }

        const base = normHouseScore(house, dasha.houseScores) * naturesgn * lvlW;
        const reinforce = transitReinforcement({ 
          targetHouse: house, 
          transits, 
          forLifeArea: feedRule.lifeAreas[0] as any // Use first life area for context
        });
        
        contrib.push(base + reinforce);
        notes.push(
          `${level.level} ${level.planet} activates H${house}; base=${(base * 100).toFixed(0)} reinf=${(reinforce * 100).toFixed(0)}`
        );
      }
    }

    const finalScore = aggregateScore(contrib);

    // Apply score thresholds
    if (feedRule.conditions.min_score !== undefined && finalScore < feedRule.conditions.min_score) {
      return { value: 0, notes: ['Score below minimum threshold'] };
    }
    if (feedRule.conditions.max_score !== undefined && finalScore > feedRule.conditions.max_score) {
      return { value: 0, notes: ['Score above maximum threshold'] };
    }

    return { value: finalScore, notes };
  };

  return {
    key: feedRule.id,
    priority: feedRule.priority,
    areas: feedRule.lifeAreas as any[],
    templateKey: feedRule.templateKey,
    customPrediction: feedRule.prediction, // Use custom prediction if provided
    remedies: feedRule.remedies,
    triggers,
    score
  };
}

/**
 * Convert all enabled user rules to executable format
 */
export async function loadExecutableUserRules(): Promise<Rule[]> {
  const userRules = await loadUserRules();
  return userRules.map(convertToExecutableRule);
}

/**
 * Get count of active user rules (for UI display)
 */
export async function getUserRuleCount(): Promise<number> {
  const rules = await loadUserRules();
  return rules.length;
}

// ===== END FILE: src/astro/feeds/userRuleLoader.ts =====
